"use client";

import React from "react";
import { useAuth } from "@clerk/nextjs";
import {
  Bell,
  LibraryBig,
  LogOut,
  Mail,
  QrCode,
  ReceiptText,
  Settings,
  Store,
} from "lucide-react";
import DecksIcon from "modules/common/components/Icons/DecksIcon";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { useNotification } from "modules/notifications/hooks/useNotification";
import {
  getChatContext,
  isChatNotification,
} from "modules/notifications/utils/notificationUtils";
import { useUserStoreNavigation } from "modules/lgs/hooks/useUserStoreNavigation";
import { useReferralInviteStore } from "modules/city/stores/useReferralInviteStore";
import { getSiteUrl } from "utils/siteUrl";

export interface AccountNavItem {
  key: string;
  text: string;
  icon: React.ReactNode;
  href?: string;
  /** Route used for active-state matching when href is dynamic (e.g. smart inbox) */
  activeHref?: string;
  /** Warm expensive destinations before navigation. */
  prefetch?: boolean;
  /** "dot" renders an indicator dot on the icon; a number renders a count badge */
  badge?: "dot" | number;
  /** Rendered as a button instead of a link (e.g. sign out) */
  onClick?: () => void;
  /** Announces that activating the item opens a dialog rather than navigating */
  hasPopup?: boolean;
  /** Rendered as a collapsible submenu (e.g. multiple stores) */
  children?: AccountNavItem[];
  /** Data still loading; render as non-interactive */
  isLoading?: boolean;
}

/**
 * Single source of truth for the signed-in account navigation, shared by the
 * desktop UserAvatar dropdown and the mobile drawer's account card so the two
 * cannot drift apart.
 */
export function useAccountNav() {
  const { userId, signOut } = useAuth();
  const { userLgs, isInitialLoading, storeCount } = useUserStoreNavigation(
    userId || ""
  );
  const {
    hasUnreadChatNotifications,
    latestChatNotification,
    hasUnreadNotifications,
    notificationCounts,
  } = useNotification();
  const openInviteModal = useReferralInviteStore((state) => state.openModal);

  // Smart routing for inbox - route to latest chat if unread
  const inboxHref =
    hasUnreadChatNotifications &&
    latestChatNotification &&
    isChatNotification(latestChatNotification)
      ? `/x/inbox/${getChatContext(latestChatNotification)?.chatId}`
      : "/x/inbox";

  // Smart routing for stores: direct link for a single store, submenu for many
  const storesItem: AccountNavItem = isInitialLoading
    ? {
        key: "stores",
        text: "Trading stores",
        icon: <Store size={ICON_SIZE} />,
        isLoading: true,
      }
    : storeCount === 1
      ? {
          key: "stores",
          href: `/x/stores/${userLgs![0].slug}`,
          text: "Trading stores",
          icon: <Store size={ICON_SIZE} />,
        }
      : storeCount > 1
        ? {
            key: "stores",
            text: "Trading stores",
            icon: <Store size={ICON_SIZE} />,
            children: userLgs!.map((store) => ({
              key: `stores-${store.slug}`,
              href: `/x/stores/${store.slug}`,
              text: store.name,
              icon: <Store size={ICON_SIZE} />,
            })),
          }
        : {
            key: "stores",
            href: "/x/stores",
            text: "Trading stores",
            icon: <Store size={ICON_SIZE} />,
          };

  // The modal resolves and waits on the referral code itself, so the entry
  // never has to fetch one just to decide whether it exists.
  const inviteItem: AccountNavItem = {
    key: "invite",
    text: "Invite friends",
    icon: <QrCode size={ICON_SIZE} />,
    hasPopup: true,
    onClick: () =>
      openInviteModal({ context: "unlocked", cityUrl: getSiteUrl() }),
  };

  const yourStuff: AccountNavItem[] = [
    {
      key: "inbox",
      href: inboxHref,
      activeHref: "/x/inbox",
      text: "Inbox",
      icon: <Mail size={ICON_SIZE} />,
      badge: hasUnreadChatNotifications ? "dot" : undefined,
    },
    {
      key: "notifications",
      href: "/x/notifications",
      text: "Notifications",
      icon: <Bell size={ICON_SIZE} />,
      badge: hasUnreadNotifications ? notificationCounts.unread : undefined,
    },
    {
      key: "binder",
      href: "/x/binder",
      text: "Binder",
      icon: <LibraryBig size={ICON_SIZE} />,
      prefetch: true,
    },
    {
      key: "decks",
      href: "/x/decks",
      text: "Your decks",
      icon: <DecksIcon fontSize={ICON_SIZE} />,
    },
    storesItem,
    {
      key: "lists",
      href: "/x/lists",
      text: "Lists",
      icon: <ReceiptText size={ICON_SIZE} />,
    },
    inviteItem,
  ];

  const settings: AccountNavItem[] = [
    {
      key: "settings",
      href: "/x/settings?tab=all",
      activeHref: "/x/settings",
      text: "Account Settings",
      icon: <Settings size={ICON_SIZE} />,
    },
    {
      key: "sign-out",
      text: "Sign out",
      icon: <LogOut size={ICON_SIZE} />,
      onClick: () => signOut(),
    },
  ];

  return { yourStuff, settings };
}
